import { withTimeout } from './withTimeout';

const STORAGE_TIMEOUT_MS = 15000;
const QUARANTINE_KEY = 'operations_quarantine';

const isValidOperation = (op) => Boolean(op?.id && op?.type);

export const createQueueError = (code, message, cause) => {
  const error = new Error(`${message} (${code})`);
  error.name = 'OfflineQueueError';
  error.code = code;
  if (cause) error.cause = cause;
  return error;
};

export const getQueueError = (error, stage = 'WRITE') => {
  if (error?.name === 'OfflineQueueError') return error;
  const reason = error?.inner || error;
  if (reason?.name === 'QuotaExceededError' || reason?.code === 22 || /quota/i.test(reason?.message || '')) {
    return createQueueError('QUEUE_STORAGE_FULL', 'Nema dovoljno prostora na uređaju za upis u red za slanje. Oslobodite prostor i pokušajte ponovo.', error);
  }
  if (reason?.name === 'DataCloneError') {
    return createQueueError('QUEUE_INVALID_DATA', 'Podaci se ne mogu sačuvati u red za slanje. Obratite se podršci.', error);
  }
  if (['InvalidStateError', 'SecurityError', 'NotFoundError', 'VersionError'].includes(reason?.name)) {
    return createQueueError('QUEUE_STORAGE_UNAVAILABLE', 'Lokalna baza uređaja nije dostupna. Sačuvajte korpu i pokušajte ponovo.', error);
  }
  if (reason?.name === 'AbortError' || reason?.name === 'TransactionInactiveError') {
    return createQueueError('QUEUE_WRITE_ABORTED', 'Uređaj je prekinuo upis u red za slanje. Pokušajte ponovo.', error);
  }
  return createQueueError(`QUEUE_${stage}_FAILED`, stage === 'READ'
    ? 'Nije moguće pročitati postojeći red za slanje. Pokušajte ponovo.'
    : 'Nije moguće sačuvati operaciju u red za slanje. Pokušajte ponovo.', error);
};

const withStorageTimeout = (promise, code, message) =>
  withTimeout(promise, STORAGE_TIMEOUT_MS, () => createQueueError(code, message));

const quarantineInvalidOperations = async (store, invalid, valid) => {
  try {
    const existing = await store.getItem(QUARANTINE_KEY);
    const quarantined = Array.isArray(existing) ? existing : [];
    await store.setItem(QUARANTINE_KEY, [...quarantined, ...invalid]);
    await store.setItem('operations', valid);
  } catch (error) {
    // Self-healing is best-effort: a quarantine write failure must never block reads.
  }
};

export const readOperationQueue = async (store, onQuarantine) => {
  let operations;
  try {
    operations = await withStorageTimeout(
      store.getItem('operations'),
      'QUEUE_READ_TIMEOUT',
      'Čitanje reda za slanje predugo traje. Provjerite uređaj i pokušajte ponovo.',
    );
  } catch (error) {
    throw getQueueError(error, 'READ');
  }
  if (operations == null) return [];

  const list = Array.isArray(operations) ? operations : [];
  const valid = list.filter(isValidOperation);
  const invalid = Array.isArray(operations)
    ? list.filter((op) => !isValidOperation(op))
    : [operations];

  if (invalid.length > 0) {
    await quarantineInvalidOperations(store, invalid, valid);
    if (typeof onQuarantine === 'function') onQuarantine(invalid.length);
  }
  return valid;
};

export const writeOperationQueue = async (store, operations) => {
  if (!Array.isArray(operations) || operations.some((op) => !isValidOperation(op))) {
    throw createQueueError('QUEUE_INVALID_OPERATION', 'Izmjena reda za slanje sadrži neispravne podatke. Obratite se podršci.');
  }
  try {
    await withStorageTimeout(
      store.setItem('operations', operations),
      'QUEUE_WRITE_TIMEOUT',
      'Upis u red za slanje predugo traje. Provjerite uređaj i pokušajte ponovo.',
    );
    return operations;
  } catch (error) {
    throw getQueueError(error);
  }
};

export const requireQueuedOperation = async (queueOperation, operation) => {
  try {
    const result = await queueOperation(operation);
    if (!result?.success) throw getQueueError(result?.error);
    return result;
  } catch (error) {
    throw getQueueError(error);
  }
};
